"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.patch = exports.head = exports.del = exports.put = exports.post = exports.get = void 0;
const adapter_core_1 = require("@aws-amplify/core/internals/adapter-core");
const publicApis_1 = require("./common/publicApis");
/**
 * GET HTTP request (server-side)
 * @param {AmplifyServer.ContextSpec} contextSpec - The context spec used to get the Amplify server context.
 * @param {GetInput} input - Input for GET operation.
 * @throws - {@link RestApiError}
 * @example
 * Send a GET request
 * ```js
 * import { get } from 'aws-amplify/api/server';
 * //...
 * const restApiResponse = await runWithAmplifyServerContext({
 *   nextServerContext: { request, response },
 *   operation: async (contextSpec) => {
 *     try {
 *       const { body } = await get(contextSpec, input).response;
 *       return await body.json();
 *     } catch (error) {
 *       console.log(error);
 *       return false;
 *     }
 *   },
 * });
 * ```
 */
const get = (contextSpec, input) => (0, publicApis_1.get)((0, adapter_core_1.getAmplifyServerContext)(contextSpec).amplify, input);
exports.get = get;
/**
 * POST HTTP request (server-side)
 * @param {AmplifyServer.ContextSpec} contextSpec - The context spec used to get the Amplify server context.
 * @param {PostInput} input - Input for POST operation.
 * @throws - {@link RestApiError}
 * @example
 * Send a POST request
 * ```js
 * import { post } from 'aws-amplify/api/server';
 * //...
 * const restApiResponse = await runWithAmplifyServerContext({
 *   nextServerContext: { request, response },
 *   operation: async (contextSpec) => {
 *     try {
 *       const { body } = await post(contextSpec, input).response;
 *       return await body.json();
 *     } catch (error) {
 *       console.log(error);
 *       return false;
 *     }
 *   },
 * });
 * ```
 */
const post = (contextSpec, input) => (0, publicApis_1.post)((0, adapter_core_1.getAmplifyServerContext)(contextSpec).amplify, input);
exports.post = post;
/**
 * PUT HTTP request (server-side)
 * @param {AmplifyServer.ContextSpec} contextSpec - The context spec used to get the Amplify server context.
 * @param {PutInput} input - Input for PUT operation.
 * @throws - {@link RestApiError}
 * @example
 * Send a PUT request
 * ```js
 * import { put } from 'aws-amplify/api/server';
 * //...
 * const restApiResponse = await runWithAmplifyServerContext({
 *   nextServerContext: { request, response },
 *   operation: async (contextSpec) => {
 *     try {
 *       const { body } = await put(contextSpec, input).response;
 *       return await body.json();
 *     } catch (error) {
 *       console.log(error);
 *       return false;
 *     }
 *   },
 * });
 * ```
 */
const put = (contextSpec, input) => (0, publicApis_1.put)((0, adapter_core_1.getAmplifyServerContext)(contextSpec).amplify, input);
exports.put = put;
/**
 * DELETE HTTP request (server-side)
 * @param {AmplifyServer.ContextSpec} contextSpec - The context spec used to get the Amplify server context.
 * @param {DeleteInput} input - Input for DELETE operation.
 * @throws - {@link RestApiError}
 * @example
 * Send a DELETE request
 * ```js
 * import { del } from 'aws-amplify/api/server';
 * //...
 * const restApiResponse = await runWithAmplifyServerContext({
 *   nextServerContext: { request, response },
 *   operation: async (contextSpec) => {
 *     try {
 *       const { headers } = await del(contextSpec, input).response;
 *     } catch (error) {
 *       console.log(error);
 *       return false;
 *     }
 *   },
 * });
 * ```
 */
const del = (contextSpec, input) => (0, publicApis_1.del)((0, adapter_core_1.getAmplifyServerContext)(contextSpec).amplify, input);
exports.del = del;
/**
 * HEAD HTTP request (server-side)
 * @param {AmplifyServer.ContextSpec} contextSpec - The context spec used to get the Amplify server context.
 * @param {HeadInput} input - Input for HEAD operation.
 * @throws - {@link RestApiError}
 * @example
 * Send a HEAD request
 * ```js
 * import { head } from 'aws-amplify/api/server';
 * //...
 * const restApiResponse = await runWithAmplifyServerContext({
 *   nextServerContext: { request, response },
 *   operation: async (contextSpec) => {
 *     try {
 *       const { headers } = await head(contextSpec, input).response;
 *     } catch (error) {
 *       console.log(error);
 *       return false;
 *     }
 *   },
 * });
 * ```
 */
const head = (contextSpec, input) => (0, publicApis_1.head)((0, adapter_core_1.getAmplifyServerContext)(contextSpec).amplify, input);
exports.head = head;
/**
 * PATCH HTTP request (server-side)
 * @param {AmplifyServer.ContextSpec} contextSpec - The context spec used to get the Amplify server context.
 * @param {PatchInput} input - Input for PATCH operation.
 * @throws - {@link RestApiError}
 * @example
 * Send a PATCH request
 * ```js
 * import { patch } from 'aws-amplify/api/server';
 * //...
 * const restApiResponse = await runWithAmplifyServerContext({
 *   nextServerContext: { request, response },
 *   operation: async (contextSpec) => {
 *     try {
 *       const { body } = await patch(contextSpec, input).response;
 *       return await body.json();
 *     } catch (error) {
 *       console.log(error);
 *       return false;
 *     }
 *   },
 * });
 * ```
 */
const patch = (contextSpec, input) => (0, publicApis_1.patch)((0, adapter_core_1.getAmplifyServerContext)(contextSpec).amplify, input);
exports.patch = patch;
